/**
 * Cursored SSE subscription (§16.4, ADR-107, AC-032).
 *
 * This used `EventSource`, and could not work. The Control API authenticates
 * every request from the `x-autonomous-team-session` header, and `EventSource`
 * has no way to set one — so the stream was refused with 401 on every attempt,
 * forever. The Console showed "office projection error" and every screen built
 * on the projection rendered nothing at all.
 *
 * The obvious repair is to put the token in the query string, which is the only
 * thing `EventSource` can carry. That is the wrong repair: a credential in a URL
 * lands in server logs, in browser history and in referrer headers, and it would
 * be the only place in this system where one does.
 *
 * So the transport is `fetch` with a streaming body, which sends headers
 * normally. The cost is parsing the SSE framing here — some forty lines, fully
 * specified by the format — and the benefit is one authentication mechanism
 * instead of two.
 *
 * The reconnect behaviour is unchanged and was the reason `EventSource` was
 * already being fought: its automatic retry is invisible, so `decideResume`
 * could not run before it, and a silently resumed stream is exactly how a gap
 * gets animated as though it were a journey (ADR-107). Every reconnect here is a
 * decision this code made.
 */

import type { SceneStreamHandlers, Subscription } from './data-source.js';

/** The projection event whose payload is a whole `OfficeScene`. */
const SCENE_EVENT = 'office.scene';

const RECONNECT_BASE_MS = 1000;
const RECONNECT_MAX_MS = 15_000;

export interface SseOptions {
  /** Base URL of the Control API, e.g. `''` for same origin. */
  readonly baseUrl: string;
  /** Called before each reconnect; returns the cursor to resume from, or `null` for a cold start. */
  readonly resumeCursor: () => string | null;
  /** Loopback session token. Sent as a header, never as a query parameter. */
  readonly sessionToken?: string | null | undefined;
  readonly firebaseToken?: (() => Promise<string | null>) | undefined;
  /**
   * The team this subscription means (§multi-team) — read at each open, so a
   * reconnect after a team switch subscribes to the team now on screen.
   */
  readonly currentTeamId?: (() => string) | undefined;
}

function parsePayload(raw: string): unknown {
  try {
    return JSON.parse(raw) as unknown;
  } catch {
    return null;
  }
}

/** One `event:`/`data:`/`id:` block, as the wire format defines it. */
interface SseMessage {
  readonly event: string;
  readonly data: string;
  readonly id: string;
}

/**
 * Splits a decoded chunk into complete messages, returning the unfinished tail.
 *
 * A message ends at a blank line and a chunk may cut one anywhere, so the
 * remainder is carried forward rather than parsed. Dropping it would silently
 * lose whichever frame happened to straddle a network packet — a failure that
 * appears only under load and looks like the server skipped an event.
 */
export function parseSseChunk(buffer: string): { readonly messages: readonly SseMessage[]; readonly rest: string } {
  const messages: SseMessage[] = [];
  let rest = buffer;

  for (;;) {
    const boundary = rest.indexOf('\n\n');
    if (boundary === -1) break;

    const block = rest.slice(0, boundary);
    rest = rest.slice(boundary + 2);

    let event = 'message';
    let id = '';
    const data: string[] = [];

    for (const line of block.split('\n')) {
      if (line.startsWith(':')) continue; // comment / keep-alive
      const colon = line.indexOf(':');
      const field = colon === -1 ? line : line.slice(0, colon);
      // One optional leading space after the colon is part of the framing.
      const value = colon === -1 ? '' : line.slice(colon + 1).replace(/^ /u, '');

      if (field === 'event') event = value;
      else if (field === 'data') data.push(value);
      else if (field === 'id') id = value;
    }

    if (data.length > 0 || event !== 'message') {
      messages.push({ event, data: data.join('\n'), id });
    }
  }

  return { messages, rest };
}

/**
 * Opens `GET /v1/events/stream` and delivers typed frames.
 *
 * The returned subscription is idempotent: closing twice is safe, and closing
 * aborts the in-flight request as well as any pending reconnect timer, so
 * navigating away cannot leave a stream quietly reopening in the background.
 */
export function subscribeToScene(
  options: SseOptions,
  initialCursor: string | null,
  handlers: SceneStreamHandlers,
): Subscription {
  let controller: AbortController | null = null;
  let closed = false;
  let attempt = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const deliver = (message: SseMessage): void => {
    if (message.event === 'snapshot') {
      // Passed through whole. The frame's payload *is* `{ scene, replayWindow }`
      // — unwrapping an outer envelope here handed the reducer a bare scene, and
      // it then looked for `.scene` on it and decoded nothing.
      handlers.onFrame({
        kind: 'snapshot',
        cursor: message.id,
        payload: parsePayload(message.data),
        resetRequired: true,
      });
      return;
    }

    if (message.event === 'reset_required') {
      const body = parsePayload(message.data);
      const reason =
        body !== null && typeof body === 'object' && 'reason' in body
          ? String((body as { reason: unknown }).reason)
          : 'the server did not give a reason';
      handlers.onFrame({ kind: 'reset_required', reason });
      /*
       * Keep reading. The snapshot arrives on *this* connection, immediately
       * after — that is what `reset_required` is announcing.
       *
       * This aborted and reconnected, which meant a cold start killed its own
       * stream a few milliseconds before the frame it was waiting for, then did
       * it again on every retry. The Console sat in RECONNECTING forever and
       * showed the reset reason as though it were the failure.
       */
      return;
    }

    handlers.onFrame({
      kind: 'event',
      cursor: message.id,
      eventType: message.event === 'message' ? 'message' : message.event,
      payload: parsePayload(message.data),
    });
  };

  const open = (cursor: string | null): void => {
    if (closed) return;

    const url = new URL(`${options.baseUrl}/v1/events/stream`, globalThis.location.href);
    url.searchParams.set('projection', 'office');
    const teamId = options.currentTeamId?.() ?? '';
    if (teamId.length > 0) url.searchParams.set('teamId', teamId);
    if (cursor !== null) url.searchParams.set('cursor', cursor);

    const abort = new AbortController();
    controller = abort;

    void (async () => {
      try {
        const firebaseToken = await options.firebaseToken?.() ?? null;
        const response = await fetch(url.toString(), {
          signal: abort.signal,
          credentials: 'same-origin',
          headers: {
            Accept: 'text/event-stream',
            ...(firebaseToken !== null
              ? { Authorization: `Bearer ${firebaseToken}` }
              : options.sessionToken ? { 'x-autonomous-team-session': options.sessionToken } : {}),
          },
        });

        if (!response.ok || response.body === null) {
          // A refused stream is reported with its status, because "401" and
          // "the stream dropped" call for completely different responses from
          // whoever is reading.
          handlers.onError(`The event stream was refused (${String(response.status)}).`);
          scheduleReconnect(false);
          return;
        }

        attempt = 0;
        handlers.onOpen();

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          const { messages, rest } = parseSseChunk(buffer);
          buffer = rest;
          for (const message of messages) deliver(message);
          if (closed) return;
        }

        if (!closed) {
          handlers.onError('The event stream ended.');
          scheduleReconnect(false);
        }
      } catch (cause) {
        // An abort is us closing, not a failure worth reporting.
        if (closed || abort.signal.aborted) return;
        handlers.onError(`The event stream dropped: ${String(cause)}`);
        scheduleReconnect(false);
      }
    })();
  };

  const scheduleReconnect = (immediate: boolean): void => {
    if (closed || timer !== null) return;
    attempt += 1;
    const delay = immediate ? RECONNECT_BASE_MS : Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * 2 ** (attempt - 1));
    timer = globalThis.setTimeout(() => {
      timer = null;
      open(options.resumeCursor());
    }, delay);
  };

  open(initialCursor);

  return {
    close(): void {
      closed = true;
      if (timer !== null) {
        globalThis.clearTimeout(timer);
        timer = null;
      }
      controller?.abort();
      controller = null;
    },
  };
}

export { SCENE_EVENT };
